import { useEffect, useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import {
  AlertOctagon,
  CheckCircle2,
  Megaphone,
  Pencil,
  Plus,
  ShieldAlert,
  Trash2,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DangerousConfirmDialog } from '@/components/ui/dangerous-confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/data-table/DataTable';
import { BulkActionBar, BulkButton } from '@/components/data-table/BulkActionBar';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import { useAllActivites } from '@/hooks/queries/useActivites';
import { useTiers } from '@/hooks/queries/useTiers';
import {
  computeKpis,
  useBulkDeleteEcheances,
  useBulkUpdateEcheanceStatut,
  useDeleteEcheance,
  useEcheances,
  useUpdateEcheanceStatut,
  type EcheancesFilter,
} from '@/hooks/queries/useEcheances';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useRole } from '@/hooks/useRole';
import { formatDA, formatDateDZ } from '@/lib/format';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import type { EcheanceRow, EcheanceSens, EcheanceStatut } from '@/lib/database.types';
import { EcheanceFormDialog } from './EcheanceFormDialog';
import { RelancerDialog } from './RelancerDialog';
import { EncaisserDialog } from './EncaisserDialog';

const STATUT_VARIANT: Record<
  EcheanceStatut,
  'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline'
> = {
  ouverte: 'outline',
  partiellement_payee: 'warning',
  payee: 'success',
  en_retard: 'warning',
  mise_en_demeure: 'destructive',
  contentieux: 'destructive',
  annulee: 'secondary',
};

interface Props {
  sens: EcheanceSens;
}

function joursRetard(dateEcheance: string): number {
  const d = new Date(dateEcheance);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.floor((today.getTime() - d.getTime()) / 86400000);
}

function rowColor(row: EcheanceRow): string {
  if (row.statut === 'payee' || row.statut === 'annulee') return '';
  const j = joursRetard(row.date_echeance);
  if (row.statut === 'mise_en_demeure' || row.statut === 'contentieux') return 'text-destructive';
  if (j > 30) return 'text-destructive';
  if (j >= 1) return 'text-orange-500';
  if (j >= -7) return 'text-warning';
  return 'text-success';
}

export function EcheancesListPage({ sens }: Props): JSX.Element {
  const isEncaisser = sens === 'a_encaisser';
  const { data: entreprises = [] } = useEntreprises();
  const { data: allActivites = [] } = useAllActivites();
  const { isAdmin, role } = useRole();

  const [entrepriseId, setEntrepriseId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 300);
  const [filter, setFilter] = useState<EcheancesFilter>({});

  useEffect(() => {
    if (!entrepriseId && entreprises[0]) setEntrepriseId(entreprises[0].id);
  }, [entreprises, entrepriseId]);

  const fullFilter: EcheancesFilter = {
    ...filter,
    entrepriseId,
    sens,
    search,
    pageSize: 200,
    page: 0,
  };

  const { data, isLoading } = useEcheances(fullFilter);
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const { data: tiers = [] } = useTiers({ entrepriseId, search: '' });

  const tierMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const tt of tiers) m.set(tt.id, tt.raison_sociale);
    return m;
  }, [tiers]);

  const updateStatut = useUpdateEcheanceStatut();
  const deleteOne = useDeleteEcheance();
  const bulkUpdate = useBulkUpdateEcheanceStatut();
  const bulkDelete = useBulkDeleteEcheances();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<EcheanceRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<EcheanceRow | null>(null);
  const [relancing, setRelancing] = useState<EcheanceRow | null>(null);
  const [encaissing, setEncaissing] = useState<EcheanceRow | null>(null);
  const [bulkConfirmDelete, setBulkConfirmDelete] = useState(false);
  const [bulkRelance, setBulkRelance] = useState(false);

  const [selectedRows, setSelectedRows] = useState<EcheanceRow[]>([]);
  const [selectionVersion, setSelectionVersion] = useState(0);
  const clearSelection = (): void => setSelectionVersion((v) => v + 1);
  const selectedIds = selectedRows.map((r) => r.id);

  const activitesScoped = useMemo(
    () => allActivites.filter((a) => a.entreprise_id === entrepriseId),
    [allActivites, entrepriseId],
  );

  const canFireJuridique = role === 'direction' || isAdmin;

  const kpis = useMemo(() => computeKpis(rows), [rows]);
  const topTierName = kpis.topTierId ? tierMap.get(kpis.topTierId) : null;

  const columns = useMemo<ColumnDef<EcheanceRow>[]>(
    () => [
      {
        accessorKey: 'reference',
        header: 'Réf.',
        size: 130,
        cell: ({ getValue }) => <span className="font-mono text-xs">{String(getValue())}</span>,
      },
      {
        accessorKey: 'tier_id',
        header: 'Tiers',
        size: 220,
        cell: ({ getValue }) => {
          const id = getValue() as string;
          return <span className="text-xs">{tierMap.get(id) ?? '—'}</span>;
        },
      },
      {
        accessorKey: 'facture_ref',
        header: 'Facture',
        size: 130,
        cell: ({ getValue }) => <span className="text-xs">{(getValue() as string) ?? '—'}</span>,
      },
      {
        accessorKey: 'date_emission',
        header: 'Émission',
        size: 100,
        cell: ({ getValue }) => (
          <span className="num text-xs">{formatDateDZ(String(getValue()))}</span>
        ),
      },
      {
        accessorKey: 'date_echeance',
        header: 'Échéance',
        size: 100,
        cell: ({ row }) => (
          <span className={cn('num text-xs font-medium', rowColor(row.original))}>
            {formatDateDZ(row.original.date_echeance)}
          </span>
        ),
      },
      {
        accessorKey: 'montant_initial',
        header: 'Montant',
        size: 110,
        cell: ({ row }) => (
          <span className="num text-xs">
            {formatDA(Number(row.original.montant_initial))}{' '}
            <span className="text-[10px] text-muted-foreground">{row.original.devise_code}</span>
          </span>
        ),
      },
      {
        accessorKey: 'montant_du',
        header: 'Restant',
        size: 110,
        cell: ({ row }) => (
          <span className={cn('num text-xs font-semibold', rowColor(row.original))}>
            {formatDA(Number(row.original.montant_du))}
          </span>
        ),
      },
      {
        id: 'jours',
        header: 'J retard',
        size: 80,
        cell: ({ row }) => {
          const j = joursRetard(row.original.date_echeance);
          if (row.original.statut === 'payee' || row.original.statut === 'annulee')
            return <span className="text-xs text-muted-foreground">—</span>;
          return (
            <span className={cn('num text-xs', rowColor(row.original))}>{j > 0 ? `+${j}` : j}</span>
          );
        },
      },
      {
        accessorKey: 'statut',
        header: 'Statut',
        size: 130,
        cell: ({ getValue }) => {
          const s = getValue() as EcheanceStatut;
          return (
            <Badge variant={STATUT_VARIANT[s]} className="whitespace-nowrap">
              {t(`echeances.statut.${s}`)}
            </Badge>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        size: 220,
        enableSorting: false,
        cell: ({ row }) => {
          const ec = row.original;
          const isClosed = ec.statut === 'payee' || ec.statut === 'annulee';
          return (
            <div className="flex flex-wrap items-center justify-end gap-1">
              {!isClosed && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setEncaissing(ec)}
                >
                  <CheckCircle2 className="h-3 w-3" /> Encaisser
                </Button>
              )}
              {isEncaisser && !isClosed && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => setRelancing(ec)}
                >
                  <Megaphone className="h-3 w-3" /> Relancer
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => {
                  setEditing(ec);
                  setDialogOpen(true);
                }}
                aria-label="Modifier"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={() => setConfirmDelete(ec)}
                  aria-label="Supprimer"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [tierMap, isAdmin, isEncaisser],
  );

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title={isEncaisser ? t('echeances.nav.aEncaisser') : t('echeances.nav.aPayer')}
        subtitle={
          isEncaisser
            ? 'Encours clients : factures émises non encore intégralement encaissées.'
            : 'Dettes fournisseurs : factures reçues non encore intégralement payées.'
        }
        actions={
          <div className="flex items-center gap-2">
            <select
              value={entrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
            <Button
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              {isEncaisser ? t('echeances.form.addEncaisser') : t('echeances.form.addPayer')}
            </Button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard
          label={isEncaisser ? t('echeances.kpi.totalAEncaisser') : t('echeances.kpi.totalAPayer')}
          value={formatDA(kpis.totalDu)}
          tone="default"
        />
        <KpiCard
          label={t('echeances.kpi.enRetard')}
          value={formatDA(kpis.totalEnRetard)}
          subtext={`${kpis.nbEnRetard} échéance(s)`}
          tone="warning"
        />
        <KpiCard
          label={t('echeances.kpi.depasse30')}
          value={formatDA(kpis.totalDepasse30)}
          tone={kpis.totalDepasse30 > 0 ? 'destructive' : 'default'}
        />
        <KpiCard
          label={isEncaisser ? t('echeances.kpi.topClient') : t('echeances.kpi.topFournisseur')}
          value={topTierName ?? '—'}
          subtext={kpis.topTierAmount > 0 ? formatDA(kpis.topTierAmount) : undefined}
          tone="default"
        />
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-card p-3">
        <FilterSelect
          label={t('echeances.filters.statut')}
          value={filter.statut ?? ''}
          onChange={(v) => setFilter((f) => ({ ...f, statut: (v as EcheanceStatut) || null }))}
          options={[
            { value: '', label: '— Tous —' },
            { value: 'ouverte', label: 'Ouverte' },
            { value: 'partiellement_payee', label: 'Partiellement payée' },
            { value: 'en_retard', label: 'En retard' },
            { value: 'mise_en_demeure', label: 'Mise en demeure' },
            { value: 'contentieux', label: 'Contentieux' },
            { value: 'payee', label: 'Payée' },
            { value: 'annulee', label: 'Annulée' },
          ]}
        />
        <FilterSelect
          label={t('echeances.filters.activite')}
          value={filter.activiteId ?? ''}
          onChange={(v) => setFilter((f) => ({ ...f, activiteId: v || null }))}
          options={[
            { value: '', label: '— Toutes —' },
            ...activitesScoped.map((a) => ({ value: a.id, label: a.nom })),
          ]}
        />
        <FilterSelect
          label={t('echeances.filters.tier')}
          value={filter.tierId ?? ''}
          onChange={(v) => setFilter((f) => ({ ...f, tierId: v || null }))}
          options={[
            { value: '', label: '— Tous —' },
            ...tiers
              .filter((tt) =>
                isEncaisser
                  ? tt.type === 'client_b2b' || tt.type === 'client_b2c'
                  : tt.type === 'fournisseur' || tt.type === 'sous_traitant',
              )
              .map((tt) => ({ value: tt.id, label: tt.raison_sociale })),
          ]}
        />
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t('echeances.filters.dateFrom')}</label>
          <Input
            type="date"
            value={filter.dateFrom ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, dateFrom: e.target.value || null }))}
            className="h-9 w-36"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">{t('echeances.filters.dateTo')}</label>
          <Input
            type="date"
            value={filter.dateTo ?? ''}
            onChange={(e) => setFilter((f) => ({ ...f, dateTo: e.target.value || null }))}
            className="h-9 w-36"
          />
        </div>
        <div className="ml-auto space-y-1">
          <label className="text-xs text-muted-foreground">{t('echeances.filters.search')}</label>
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Réf, facture, motif…"
            className="h-9 w-56"
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setFilter({});
            setSearchInput('');
          }}
        >
          <XCircle className="h-3.5 w-3.5" /> Effacer
        </Button>
      </div>

      <DataTable
        key={selectionVersion}
        data={rows}
        columns={columns}
        loading={isLoading}
        pageSize={50}
        searchPlaceholder="Rechercher…"
        exportFileName={`echeances-${sens}.csv`}
        enableSelection
        getRowId={(row) => row.id}
        onSelectionChange={setSelectedRows}
        bulkBar={
          <BulkActionBar count={selectedRows.length} onClear={clearSelection}>
            {isEncaisser && (
              <BulkButton onClick={() => setBulkRelance(true)}>
                <Megaphone className="h-3.5 w-3.5" /> Relancer
              </BulkButton>
            )}
            <BulkButton
              onClick={async () => {
                const r = await bulkUpdate.mutateAsync({
                  ids: selectedIds,
                  statut: 'payee',
                });
                toast.success(`${r.success.length} payée(s)`);
                clearSelection();
              }}
            >
              <CheckCircle2 className="h-3.5 w-3.5" /> Marquer payées
            </BulkButton>
            {canFireJuridique && isEncaisser && (
              <BulkButton
                onClick={async () => {
                  const r = await bulkUpdate.mutateAsync({
                    ids: selectedIds,
                    statut: 'mise_en_demeure',
                  });
                  if (r.errors.length === 0)
                    toast.success(`${r.success.length} en mise en demeure`);
                  else
                    toast.warning(
                      `${r.success.length} OK · ${r.errors.length} échec(s) — ${r.errors[0]?.message ?? ''}`,
                    );
                  clearSelection();
                }}
              >
                <AlertOctagon className="h-3.5 w-3.5" /> Mise en demeure
              </BulkButton>
            )}
            {canFireJuridique && isEncaisser && (
              <BulkButton
                variant="destructive"
                onClick={async () => {
                  const r = await bulkUpdate.mutateAsync({
                    ids: selectedIds,
                    statut: 'contentieux',
                  });
                  if (r.errors.length === 0) toast.success(`${r.success.length} en contentieux`);
                  else
                    toast.warning(
                      `${r.success.length} OK · ${r.errors.length} échec(s) — ${r.errors[0]?.message ?? ''}`,
                    );
                  clearSelection();
                }}
              >
                <ShieldAlert className="h-3.5 w-3.5" /> Contentieux
              </BulkButton>
            )}
            {isAdmin && (
              <BulkButton variant="destructive" onClick={() => setBulkConfirmDelete(true)}>
                <Trash2 className="h-3.5 w-3.5" /> Supprimer
              </BulkButton>
            )}
          </BulkActionBar>
        }
      />

      <div className="text-xs text-muted-foreground">
        {rows.length} échéance{rows.length > 1 ? 's' : ''} · total dû{' '}
        <span className="num font-medium">{formatDA(kpis.totalDu)}</span>
      </div>

      <EcheanceFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        entrepriseId={entrepriseId}
        sens={sens}
        editing={editing}
        onSaved={() => setDialogOpen(false)}
      />

      <EncaisserDialog
        open={!!encaissing}
        onOpenChange={(o) => !o && setEncaissing(null)}
        echeance={encaissing}
        onSaved={() => setEncaissing(null)}
      />

      <RelancerDialog
        open={!!relancing || bulkRelance}
        onOpenChange={(o) => {
          if (!o) {
            setRelancing(null);
            setBulkRelance(false);
          }
        }}
        targets={(relancing
          ? [relancing]
          : bulkRelance
            ? selectedRows.filter((r) => r.statut !== 'payee' && r.statut !== 'annulee')
            : []
        ).map((row) => ({ kind: 'echeance' as const, row }))}
        entrepriseId={entrepriseId}
        onSent={() => {
          setRelancing(null);
          setBulkRelance(false);
          if (bulkRelance) clearSelection();
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={`Supprimer l'échéance « ${confirmDelete?.reference} » ?`}
        description="Cette action est irréversible. Réservée aux super_admin."
        loading={deleteOne.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await deleteOne.mutateAsync(confirmDelete.id);
          toast.success('Échéance supprimée');
          setConfirmDelete(null);
        }}
      />

      <DangerousConfirmDialog
        open={bulkConfirmDelete}
        onOpenChange={setBulkConfirmDelete}
        title={`Supprimer ${selectedRows.length} échéance${selectedRows.length > 1 ? 's' : ''} ?`}
        description="Action irréversible. Les relances liées seront aussi supprimées."
        consequences={[
          `${selectedRows.length} ligne(s) effacées`,
          'Les relances liées sont supprimées en cascade',
          "L'action est journalisée dans audit_log",
        ]}
        challenge="SUPPRIMER"
        loading={bulkDelete.isPending}
        onConfirm={async () => {
          const r = await bulkDelete.mutateAsync(selectedIds);
          toast.success(`${r.success.length} ligne(s) supprimée(s)`);
          setBulkConfirmDelete(false);
          clearSelection();
        }}
      />

      {/* Used to silence unused-import warning in some configs */}
      <span className="hidden">{updateStatut.isPending ? '' : ''}</span>
    </div>
  );
}

function KpiCard({
  label,
  value,
  subtext,
  tone,
}: {
  label: string;
  value: string;
  subtext?: string;
  tone: 'default' | 'warning' | 'destructive';
}): JSX.Element {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={cn(
          'num text-xl font-semibold',
          tone === 'warning' && 'text-warning',
          tone === 'destructive' && 'text-destructive',
        )}
      >
        {value}
      </div>
      {subtext && <div className="text-xs text-muted-foreground">{subtext}</div>}
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}): JSX.Element {
  return (
    <div className="space-y-1">
      <label className="text-xs text-muted-foreground">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}
